import { Alert, CircularProgress, Stack } from '@mui/material';
import { t } from 'i18next';
import { ReactNode, memo, useEffect, useState } from 'react';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { Interpolation } from '../../../config/i18n/components/Interpolation';
import { PATHS } from '../../../routing/routes';
import { Report, ReportDone, ReportFailed, ReportPending } from '../models/Report';

type Props = {
  reportId: number;
  loader: (reportId: number) => Promise<Report | null>;
  children: ReportLoaderChildren;
};

export type ReportLoaderChildren = (report: Report | null, loading: boolean) => ReactNode;

export const ReportLoader = memo(({ loader, children, reportId }: Props) => {
  const [report, setReport] = useState<Report | null>(null);

  useEffect(() => {
    let mounted = true;
    let timeout: NodeJS.Timeout | null = null;
    const load = async () => {
      const r = await loader(reportId);
      if (r instanceof ReportPending) {
        timeout = setTimeout(load, 5000);
      }
      if (mounted) {
        setReport(r);
      }
    };
    setReport(null);
    load();
    return () => {
      mounted = false;
      if (timeout !== null) {
        clearTimeout(timeout);
        timeout = null;
      }
    };
  }, [loader, reportId]);

  return (
    <Stack spacing={2}>
      {report instanceof ReportPending && (
        <Alert icon={<CircularProgress size={20} />} color="info">
          {t('reportPendingMessage')}
        </Alert>
      )}
      {report instanceof ReportFailed && (
        <Alert color="error" sx={{ whiteSpace: 'pre-line' }}>
          {t('reportFailedMessage', { reason: report.getReason() })}
        </Alert>
      )}
      {report instanceof ReportDone && report.getActiveExceptionsCount() > 0 && (
        <Alert color="info">
          <Interpolation
            i18nKey="exceptionsAreActiveInTheProjectInstance"
            items={[{ name: 'anchor', href: UrlHelper.generatePath(PATHS.PROJECT_FILTERS, { projectId: report.getProject()?.getId().toString() }) }]}
          />
        </Alert>
      )}
      {children(report, !report || report instanceof ReportPending)}
    </Stack>
  );
});
